import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import PropertyRequestSummary from "../components/propertyRequest/PropertyRequestSummary";
import { MatchCard } from "../components/propertyRequest/PropertyRequestMatches";
import { fieldClass } from "../components/propertyRequest/PropertyRequestFields";
import { categoryLabel, requestTitle } from "../components/propertyRequest/labels";
import { fromCriteria } from "../utils/propertyRequestForm";
import { propertyRequestApi } from "../Services/propertyRequestService";

const MAX_MESSAGE = 500;

// "Proposer un bien": the signed-in advertiser picks one of HIS OWN active listings (listed by the API from the token).
export default function PropertyRequestPropose() {
  const { id } = useParams();
  const { t } = useTranslation();
  const [request, setRequest] = useState(null);
  const [data, setData] = useState({ loading: true, error: "", ownRequest: false, status: "", items: [] });
  const [selected, setSelected] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState({ error: "", success: "" });
  const busy = useRef(false);

  const load = useCallback(async () => {
    try {
      const result = await propertyRequestApi.candidates(id);
      setData({ loading: false, error: "", ownRequest: false, status: result.requestStatus, items: result.items || [] });
    } catch (error) {
      setData({ loading: false, error: error.code === "own_request" ? "" : error.message, ownRequest: error.code === "own_request", status: "", items: [] });
    }
  }, [id]);

  useEffect(() => {
    propertyRequestApi.get(id).then(setRequest).catch((error) => setData((d) => ({ ...d, loading: false, error: error.message })));
    load();
  }, [id, load]);

  const open = data.status === "Active";
  const key = (item) => `${item.listing.kind}:${item.listing.id}`;

  const submit = async (event) => {
    event.preventDefault();
    if (busy.current) return;
    const item = data.items.find((x) => key(x) === selected);
    if (!item) { setFeedback({ error: t("propertyRequest.errors.chooseListing"), success: "" }); return; }
    busy.current = true; setSending(true); setFeedback({ error: "", success: "" });
    try {
      await propertyRequestApi.propose(id, { listingKind: item.listing.kind, listingId: item.listing.id, message });
      setFeedback({ error: "", success: t("propertyRequest.propose.sent") });
      setSelected(""); setMessage("");
      await load();
    } catch (error) {
      setFeedback({ error: error.message, success: "" });
      if (error.code === "request_not_active") await load();
    } finally {
      busy.current = false; setSending(false);
    }
  };

  const withdraw = async (item) => {
    try {
      await propertyRequestApi.withdraw(item.proposalId);
      toast.success(t("propertyRequest.propose.withdrawn"));
      await load();
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <main className="lina-property-request mx-auto w-full max-w-3xl px-4 py-6 sm:px-6 sm:py-10">
      <Link to={`/property-request/${id}`} className="text-sm font-semibold text-green-800 underline">{t("propertyRequest.detail.back")}</Link>
      <h1 className="mt-3 text-3xl font-black text-slate-900 sm:text-4xl">{t("propertyRequest.propose.title")}</h1>
      {request && (
        <div className="mt-4 space-y-2">
          <p className="text-base font-semibold text-slate-700">{requestTitle(t, request)}</p>
          <PropertyRequestSummary criteria={fromCriteria(request)} text={request.freeText || ""} />
        </div>
      )}
      {data.loading && <p role="status" className="mt-6 text-slate-600">{t("propertyRequest.mine.loading")}</p>}
      {data.error && <p role="alert" className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800">{data.error}</p>}
      {data.ownRequest && <p role="alert" className="mt-6 rounded-2xl bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900">{t("propertyRequest.propose.ownRequest")}</p>}
      {!data.loading && !data.ownRequest && !data.error && (
        <form className="mt-6 space-y-4" noValidate onSubmit={submit}>
          {!open && <p role="alert" className="rounded-2xl bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900" data-closed>{t("propertyRequest.propose.closed")}</p>}
          <fieldset className="space-y-3" disabled={!open || sending}>
            <legend className="text-lg font-bold text-slate-900">{t("propertyRequest.propose.choose")}</legend>
            <p className="text-sm text-slate-600">{t("propertyRequest.propose.onlyOwn")} {t("propertyRequest.propose.scoreNotice")}</p>
            {data.items.length === 0 && (
              <p className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-700">
                {request?.transaction && request.categories?.length
                  ? t("propertyRequest.propose.noneFor", {
                    type: request.categories.map((c) => categoryLabel(t, c, request.transaction)).join(" / "),
                    transaction: t(`propertyRequest.transactionShort.${request.transaction}`),
                  })
                  : t("propertyRequest.propose.none")} <Link to="/createbien" className="font-semibold text-green-800 underline">{t("propertyRequest.propose.createListing")}</Link>
              </p>
            )}
            {data.items.map((item) => {
              const proposed = Boolean(item.proposalId);
              return (
                <div key={key(item)} className={`rounded-3xl border-2 ${selected === key(item) ? "border-green-700" : "border-transparent"}`} data-candidate={item.listing.id}>
                  <MatchCard match={item} aside={proposed ? <span className="rounded-full bg-slate-800 px-3 py-1 text-xs font-bold text-white">{t("propertyRequest.propose.alreadyProposed")}</span> : null}>
                    <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 px-4 py-3">
                      {proposed ? (
                        <button type="button" className="lina-action-danger min-h-[44px]" onClick={() => withdraw(item)}>{t("propertyRequest.propose.withdraw")}</button>
                      ) : (
                        <label className="flex min-h-[44px] cursor-pointer items-center gap-2 text-sm font-semibold text-slate-900">
                          <input type="radio" name="pr-listing" value={key(item)} checked={selected === key(item)} onChange={() => setSelected(key(item))}
                            className="h-5 w-5 accent-green-700" />
                          {t("propertyRequest.propose.select", { title: item.listing.title || "" })}
                        </label>
                      )}
                    </div>
                  </MatchCard>
                </div>
              );
            })}
          </fieldset>
          {data.items.some((x) => !x.proposalId) && (
            <div>
              <label htmlFor="pr-proposal-message" className="block text-sm font-semibold text-slate-800">{t("propertyRequest.propose.message")}</label>
              <textarea id="pr-proposal-message" rows={3} maxLength={MAX_MESSAGE} value={message} disabled={!open || sending}
                onChange={(e) => setMessage(e.target.value)} placeholder={t("propertyRequest.propose.messagePlaceholder")} className={`${fieldClass} resize-y`} />
              <div className="mt-1 flex justify-between gap-2 text-xs text-slate-500">
                <span>{t("propertyRequest.propose.messageNotice")}</span><bdi dir="ltr">{message.length} / {MAX_MESSAGE}</bdi>
              </div>
            </div>
          )}
          {feedback.error && <p role="alert" className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800">{feedback.error}</p>}
          {feedback.success && <p role="status" className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-semibold text-green-900">{feedback.success}</p>}
          {data.items.some((x) => !x.proposalId) && (
            <button type="submit" className="lina-action-primary h-12 w-full text-base" disabled={!open || sending || !selected}>
              {sending ? t("propertyRequest.propose.sending") : t("propertyRequest.propose.submit")}
            </button>
          )}
        </form>
      )}
    </main>
  );
}
